/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    useContext,
    useState,
    useCallback,
    lazy,
    Suspense,
    type ComponentPropsWithoutRef,
    type ReactElement,
    type ReactNode,
} from 'react';
import { Await } from 'react-router';
import { NavLink } from '@/components/link';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { Button } from '@/components/ui/button';
import { MenuIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NavigationMenuLink, navigationMenuTriggerStyle } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { routes, routeHref } from '@/route-paths';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';
import { getRegionIds } from '@/lib/decorators/region-definition';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import MainNavigationBar from '@/components/main-navigation/main-navigation-bar';
import type { NavItem } from '@/lib/header-menu/types';

const MobileMenuSheet = lazy(() => import('./mobile-menu-sheet'));

@Component('megaMenu', {
    name: 'Mega Menu',
    group: 'Layout',
    description: 'Site-wide mega menu with per-category dropdown panel content slots',
    embedded: true,
    component_id: 'mega-menu',
})
@RegionDefinition([
    // One region per top-level category, keyed by category id via the `region_<id>` convention.
    // Edit this list to match your catalog's top-level category ids. Must stay an array literal —
    // the cartridge generator extracts regions via AST and only handles array-literal arguments.
    { id: 'region_women', name: 'Women' },
    { id: 'region_men', name: 'Men' },
    { id: 'region_kids', name: 'Kids' },
])
// oxlint-disable-next-line react-refresh/only-export-components
export class MegaMenuMetadata {}

/**
 * Declared mega-menu region ids, derived from the `@RegionDefinition` decorator above so the
 * decorator list is the single source of truth. A top-level category renders its region when
 * `region_${category.id}` is present in this set.
 */
export const MEGA_MENU_REGION_IDS: ReadonlySet<string> = new Set(getRegionIds(MegaMenuMetadata));

/**
 * Resolves the embedded-component region id for a top-level category, or `undefined` when the
 * category has no mapped region (no embedded component, no category id, or the derived
 * `region_${categoryId}` is not declared in {@link MEGA_MENU_REGION_IDS}). Pure and exported so the
 * per-category mapping can be unit-tested without mounting the lazily-rendered dropdown panel.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function resolveMegaMenuRegionId(
    categoryId: string | undefined,
    hasEmbeddedComponent: boolean,
    regionIds: ReadonlySet<string> = MEGA_MENU_REGION_IDS
): string | undefined {
    if (!hasEmbeddedComponent || categoryId === undefined) return undefined;
    const regionId = `region_${categoryId}`;
    return regionIds.has(regionId) ? regionId : undefined;
}

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
    /** Header menu items (header `navigation` Page Designer region); empty means the category menu is shown. */
    navItems: NavItem[];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

// oxlint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const imageSrc = toImageUrl({ src: (category?.c_slotBannerImage as string) ?? '', config });

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={routeHref(routes.category, { categoryId: category.id })}>
                {imageSrc ? (
                    <img
                        className="object-contain w-full max-w-full max-h-[512px]"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // oxlint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

/**
 * True when the resolved embedded component actually holds authored content in `regionId`.
 * A declared-but-empty region returns false so the panel can fall through to the banner.
 * Pure and exported so the region-vs-banner precedence can be unit-tested without mounting
 * the lazily-rendered dropdown.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function regionHasContent(resolved: ComponentWithComponentData | null, regionId: string): boolean {
    return (resolved?.regions?.find((r) => r.id === regionId)?.components?.length ?? 0) > 0;
}

/**
 * Right-column "featured content" for a top-level category's dropdown panel.
 *
 * Precedence: a populated embedded region (`region_<category.id>`) wins; otherwise the
 * legacy `c_headerMenuBanner`; otherwise nothing.
 *
 * A *declared but empty* region must NOT win over the banner and must NOT emit a labelled
 * `<aside>` — a complementary landmark with no content is screen-reader noise (WCAG 1.3.1).
 * Because the embedded component is streamed from the loader, whether a region actually
 * holds authored content is only known once the promise resolves, so the populated check
 * (`regionHasContent`) happens inside Await. Design-mode authoring of these regions runs
 * through the mini-PD component-preview route (page mode), not this slot, so no design-mode
 * branch is needed here.
 */
function MegaMenuFeaturedSlot({
    category,
    regionId,
    embeddedComponent,
    label,
}: {
    category: ShopperProducts.schemas['Category'];
    regionId: string | undefined;
    embeddedComponent: EmbeddedMegaMenuComponent;
    label: string;
}): ReactNode {
    const bannerSlot = hasBanner(category) ? (
        <aside className="self-stretch" aria-label={label}>
            <CategoryBanner category={category} />
        </aside>
    ) : null;

    // No region mapped for this category (or no embedded component was fetched):
    // the banner is the only possible featured content.
    if (!regionId || embeddedComponent === undefined) {
        return bannerSlot;
    }

    const renderResolved = (resolved: ComponentWithComponentData | null): ReactNode => {
        if (!regionHasContent(resolved, regionId)) {
            return bannerSlot;
        }
        return (
            <aside className="self-stretch" aria-label={label}>
                <EmbeddedComponentRegion component={resolved} regionId={regionId} />
            </aside>
        );
    };

    if (embeddedComponent instanceof Promise) {
        // Below-the-fold panel; the promise is fetched at route load and is almost always
        // resolved before the dropdown opens, so `fallback={null}` avoids a banner→region flash.
        return (
            <Suspense fallback={null}>
                <Await resolve={embeddedComponent} errorElement={bannerSlot}>
                    {renderResolved}
                </Await>
            </Suspense>
        );
    }
    return renderResolved(embeddedComponent);
}

function ShopAllCategoryLink({ category }: { category: ShopperProducts.schemas['Category'] }): ReactElement {
    const { t } = useTranslation('header');
    return (
        <NavigationMenuLink asChild>
            <NavLink
                to={routeHref(routes.category, { categoryId: category.id })}
                // When the panel has a featured column it is a 2-col grid whose other
                // children are the submenu list and the banner/region aside. Span both
                // columns so this link sits on its own row above them and the list and
                // banner stay side by side. On panels with no featured column the
                // container is not a grid, so col-span is inert.
                className="block md:col-span-2 text-sm font-medium leading-5 underline underline-offset-4 hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/60 focus:!text-header-menu-foreground/60 transition-colors">
                {t('shopAllCategory', {
                    category: category.name,
                    defaultValue: `Shop all ${category.name}`,
                })}
            </NavLink>
        </NavigationMenuLink>
    );
}

// MobileMenuDropdown - Mounts the mobile hamburger drawer.
export function MobileMenuDropdown(): ReactElement | null {
    const context = useMobileMenu();
    const [hasOpened, setHasOpened] = useState(false);

    if (context?.isOpen && !hasOpened) {
        setHasOpened(true);
    }

    if (!context || !hasOpened) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <MobileMenuSheet
                open={context.isOpen}
                onOpenChange={(open) => {
                    if (!open) context.close();
                }}
                categories={context.categories}
                navItems={context.navItems}
            />
        </Suspense>
    );
}

type EmbeddedMegaMenuComponent = ComponentWithComponentData | Promise<ComponentWithComponentData | null> | undefined;

interface ResponsiveNavigationMenuProps extends ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> {
    /**
     * Embedded mega-menu Page Designer component fetched by the route loader via
     * `fetchComponentWithComponentData({ componentId: 'mega-menu' })`. A top-level category whose
     * id matches a declared region (`region_${category.id}`, e.g. `region_womens`) renders that
     * region in its dropdown panel; merchants place content blocks (image, hero, etc.) into those
     * regions in Page Designer. Categories without a matching region fall back to the header banner.
     */
    embeddedComponent?: EmbeddedMegaMenuComponent;
    /**
     * Header menu items mapped by the route loader from the header's `navigation` Page Designer region. When it
     * resolves to at least one item, the menu renders those items (desktop bar + mobile sheet);
     * otherwise (empty, missing or failed) it falls back to the root-category mega-menu.
     */
    headerMenu?: Promise<NavItem[]>;
}

/**
 * Preserve the header row's responsive dimensions while the root navigation data suspends.
 * The desktop placeholder fills the stable navigation slot so resolving the menu cannot
 * redistribute the header's remaining horizontal space during hydration.
 */
function ResponsiveNavigationMenuFallback(): ReactElement {
    return (
        <>
            <div aria-hidden="true" className="lg:hidden size-9 shrink-0" />
            <div aria-hidden="true" className="hidden lg:block h-full w-full" />
        </>
    );
}

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for both mobile and desktop:
 * - On mobile (< 1024px): Hamburger button with expandable vertical menu
 * - On desktop (>= 1024px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @param props.embeddedComponent - Optional Page Designer 'mega-menu' component data
 * @param props.headerMenu - Optional header menu items from the header's `navigation` region
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
export default function ResponsiveNavigationMenu({
    headerMenu,
    ...props
}: ResponsiveNavigationMenuProps): ReactElement {
    if (!headerMenu) {
        return <ResponsiveNavigationMenuView {...props} navItems={[]} />;
    }
    // One Suspense boundary per deferred promise: this one for the header menu; the category
    // tree keeps its own boundary inside WithCategoryNavigationMenu.
    return (
        <Suspense fallback={<ResponsiveNavigationMenuFallback />}>
            <Await resolve={headerMenu} errorElement={<ResponsiveNavigationMenuView {...props} navItems={[]} />}>
                {(items) => <ResponsiveNavigationMenuView {...props} navItems={items} />}
            </Await>
        </Suspense>
    );
}

function ResponsiveNavigationMenuView({
    resolve,
    defer,
    embeddedComponent,
    navItems,
}: Omit<ResponsiveNavigationMenuProps, 'headerMenu'> & { navItems: NavItem[] }): ReactElement {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { t } = useTranslation('header');

    const defaultListStyle = {
        width: '100%',
        maxWidth: '100%',
    };

    // Element props generator
    const getElementProps = useCallback(
        ({ level }: { level: number; category: ShopperProducts.schemas['Category']; isLeaf?: boolean }) => {
            const isSubcategory = level >= 1;

            // A top-level category that has a submenu renders as a disclosure trigger
            // (a button with aria-expanded). Activating it opens the submenu for both
            // mouse and keyboard, so the action always matches the announced role.
            // We deliberately do not navigate on activation: a control announced as
            // "expandable" that also changes context on click fails WCAG 3.2.2
            // (Context changes on input). The category landing page stays reachable
            // through the panel's links and banner.
            if (!isSubcategory) {
                return {
                    className: cn(
                        navigationMenuTriggerStyle(),
                        'h-8 px-1.5 py-0 text-base font-medium leading-6 text-header-menu-foreground [--ui-radius:var(--radius-lg)]'
                    ),
                };
            }

            return {
                className:
                    'text-sm font-medium leading-5 hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/60 focus:!text-header-menu-foreground/60 transition-colors',
            };
        },
        []
    );

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer} fallback={<ResponsiveNavigationMenuFallback />}>
            {({ categories }) => {
                const mobileMenuContext: MobileMenuContextType = {
                    isOpen: mobileMenuOpen,
                    toggle: () => setMobileMenuOpen(!mobileMenuOpen),
                    close: () => setMobileMenuOpen(false),
                    categories,
                    navItems,
                };

                const regionIdFor = (categoryId: string | undefined): string | undefined =>
                    resolveMegaMenuRegionId(categoryId, embeddedComponent !== undefined);

                return (
                    <MobileMenuContext.Provider value={mobileMenuContext}>
                        {/* Mobile: Hamburger button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={mobileMenuContext.toggle}
                            className="lg:hidden text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity"
                            aria-label={t('openMenu', 'Open menu')}
                            aria-haspopup="dialog"
                            aria-expanded={mobileMenuOpen}>
                            <MenuIcon className="size-5" />
                        </Button>

                        {/* Desktop: Mega menu (always rendered, hidden on mobile with CSS) */}
                        <div className="hidden lg:flex h-full w-full items-center">
                            {navItems.length > 0 ? (
                                <MainNavigationBar items={navItems} />
                            ) : (
                                <CategoryNavigationMenu
                                    categories={categories}
                                    delayDuration={0}
                                    propsViewport={() => ({
                                        className:
                                            ' border-0 shadow-lg [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none will-change-transform',
                                        // Anchor the fixed panel to both viewport edges via `left: 0` + `right: 0`
                                        // so its width matches the layout viewport, *excluding* the scrollbar gutter.
                                        // Using `width: 100vw` instead would include the scrollbar and overshoot
                                        // any in-flow content (e.g. the header) by the scrollbar's width.
                                        style: {
                                            position: 'fixed',
                                            top: 'var(--header-height)',
                                            left: 0,
                                            right: 0,
                                        },
                                    })}
                                    propsContentContainer={() => ({
                                        className:
                                            '!p-0 !left-auto !right-auto !w-full md:!w-full !animate-none !transition-none',
                                    })}
                                    propsContent={({ category }) => {
                                        const hasRegion = regionIdFor(category.id) !== undefined;
                                        const showRightColumn = hasRegion || hasBanner(category);
                                        return {
                                            className: cn(
                                                'section-container pb-6',
                                                showRightColumn &&
                                                    (isVertical(category)
                                                        ? 'grid md:grid-cols-[1fr_.3fr] items-start'
                                                        : 'grid md:grid-cols-[1fr_.6fr] items-start')
                                            ),
                                        };
                                    }}
                                    propsList={({ parent, categories: subCategories, level }) => {
                                        if (level === 0) {
                                            return { className: 'gap-6' };
                                        }
                                        if (level === 1) {
                                            if (isVertical(parent)) {
                                                return {
                                                    style: defaultListStyle,
                                                    className: 'flex flex-col gap-0 p-0',
                                                };
                                            }
                                            return {
                                                style: {
                                                    ...defaultListStyle,
                                                    gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                                                },
                                                className: 'grid p-0',
                                            };
                                        }
                                    }}
                                    propsElement={getElementProps}
                                    renderSlotListBefore={({ level, parent }) => {
                                        // The top-level trigger only opens the panel (it never
                                        // navigates, per WCAG 3.2.2). Give every panel an explicit
                                        // link to the parent category's landing page so it stays
                                        // reachable whether or not the category has a banner.
                                        if (level === 1 && parent) {
                                            return <ShopAllCategoryLink category={parent} />;
                                        }
                                    }}
                                    renderSlotListAfter={({ level, parent }) => {
                                        if (level !== 1 || !parent) return null;
                                        // The dropdown renders multiple complementary landmarks (one per open
                                        // category), so each <aside> needs a distinct accessible name for screen
                                        // reader users to tell them apart (WCAG 1.3.1). Categories with neither a
                                        // populated region nor a banner render nothing — an empty landmark would
                                        // only add screen reader noise (handled inside MegaMenuFeaturedSlot).
                                        return (
                                            <MegaMenuFeaturedSlot
                                                category={parent}
                                                regionId={regionIdFor(parent.id)}
                                                embeddedComponent={embeddedComponent}
                                                label={t('featuredContent', {
                                                    category: parent.name,
                                                    defaultValue: `${parent.name} featured content`,
                                                })}
                                            />
                                        );
                                    }}
                                />
                            )}
                        </div>

                        {/* Mobile: Menu dropdown (rendered here to be inside provider) */}
                        <MobileMenuDropdown />
                    </MobileMenuContext.Provider>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
